import classnames from 'classnames';
import * as React from 'react';

import { Omit } from '../../../util/types';
import { getTextHeight } from '../competency-bars/common';

const styles = require('./svg-builder.css');

/**
 * SVG Builder for Futurefit graphing
 * Expected behaviour is that coordinates (e.g. x, y) scale with the image while
 * other properties (like radius) stay constant.
 */
export class SVGBuilder {
  private readonly strokeWidth: number;
  private readonly viewBox: ViewBox;

  private readonly drawables: Drawable[];

  /// FF2-436 Count labels, and infoIcons and rects (see CompetencyBars), so that we can reference them in walkthroughs independent of what's in them
  private labelCount: number = 0;
  private customItemCount: number = 0;
  // Only increment for progress bars - skills - actual score
  private rectCount: number = 0;

  constructor(strokeWidth: number, viewBox?: ViewBox) {
    this.strokeWidth = strokeWidth;
    this.drawables = [];
    this.viewBox = viewBox
      ? viewBox
      : {
        posX: 0,
        posY: 0,
        dimX: 100,
        dimY: 100
      };
  }

  incrementCustomItemCount(): number {
    this.customItemCount++;
    return this.customItemCount;
  }
  incrementRectCount(): number {
    this.rectCount++;
    return this.rectCount;
  }

  addLine(l: LineProps) {
    const line: Line = {
      kind: 'line',
      x1: l.x1,
      y1: l.y1,
      x2: l.x2,
      y2: l.y2
    };
    if (l.style === 'primary') {
      line.className = styles.primary;
    } else if (l.style === 'dotted') {
      line.strokeDashArray = '2 4';
    } else if (l.style === 'dashed') {
      line.strokeDashArray = '4 2';
    }
    if (l.stroke) {
      line.className = classnames(line.className, styles[l.stroke]);
    }
    this.drawables.push(line);
    return this;
  }

  addCircle(c: CircleProps) {
    const circle: Circle = {
      kind: 'circle',
      r: c.r,
      x: c.x,
      y: c.y
    };
    if (c.style === 'filled') {
      circle.className = styles.filled;
    } else if (c.style === 'shaded') {
      circle.className = styles.shaded;
    } else if (c.style === 'background') {
      circle.className = styles.background;
    }
    this.drawables.push(circle);
    return this;
  }

  addTriangle(t: TriangleProps) {
    const p1 = {
      x: t.x - 1 * t.size,
      y: t.y - 1 * t.size
    };
    const p2 = {
      x: t.x + 1 * t.size,
      y: t.y
    };
    const p3 = {
      x: t.x - 1 * t.size,
      y: t.y + 1 * t.size
    };
    this.drawables.push({ kind: 'triangle', p1, p2, p3 });
    return this;
  }

  anchorToProps(anchor: Anchor): { dominantBaseline: DominantBaseline; textAnchor: TextAnchor } {
    if (anchor === 'center') {
      return {
        dominantBaseline: 'middle',
        textAnchor: 'middle'
      };
    } else if (anchor === 'top') {
      return {
        dominantBaseline: 'baseline',
        textAnchor: 'middle'
      };
    } else if (anchor === 'bottom') {
      return {
        dominantBaseline: 'hanging',
        textAnchor: 'middle'
      };
    } else if (anchor === 'left') {
      return {
        dominantBaseline: 'middle',
        textAnchor: 'end'
      };
    } else if (anchor === 'right') {
      return {
        dominantBaseline: 'middle',
        textAnchor: 'start'
      };
    } else if (anchor === 'bottomleft') {
      return {
        dominantBaseline: 'hanging',
        textAnchor: 'end'
      };
    } else if (anchor === 'bottomright') {
      return {
        dominantBaseline: 'hanging',
        textAnchor: 'start'
      };
    } else if (anchor === 'topleft') {
      return {
        dominantBaseline: 'baseline',
        textAnchor: 'end'
      };
    } else if (anchor === 'topright') {
      return {
        dominantBaseline: 'baseline',
        textAnchor: 'start'
      };
    } else {
      throw Error('Unimplemented anchor');
    }
  }

  addLabel(l: LabelProps) {
    this.labelCount++;
    const label: Label = {
      kind: 'label',
      text: l.text,
      subtext: l.subtext,
      x: l.x,
      y: l.y,
      onClick: l.onClick,
      id: this.labelCount
    };
    label.dominantBaseline = this.anchorToProps(l.anchor).dominantBaseline;
    label.textAnchor = this.anchorToProps(l.anchor).textAnchor;
    label.className = styles[l.style];
    const textHeight = getTextHeight(label.text, '700 12px Lato');
    const subtextHeight = getTextHeight(label.text, '8px Open SansRegular');
    const gap = 7;
    if (label.dominantBaseline === 'baseline') {
      label.subtextDy = `-${textHeight + gap}px`;
    } else if (label.dominantBaseline === 'hanging') {
      label.textDy = `${subtextHeight + gap}px`;
    } else {
      label.subtextDy = `-${(subtextHeight + gap) / 2}px`;
      label.textDy = `${(textHeight + gap) / 2}px`;
    }
    this.drawables.push(label);
    return this;
  }

  addRect(r: RectProps) {
    const rect: Rect = {
      kind: 'rect',
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      className: r.isVisible === false ? classnames(styles[r.style], styles.notVisible) : styles[r.style],
      id: r.id
    };
    rect.onMouseEnter = r.onMouseEnter;
    rect.onMouseLeave = r.onMouseLeave;
    this.drawables.push(rect);
    return this;
  }

  addText(t: TextProps) {
    const text: Text = {
      kind: 'text',
      text: t.text,
      x: t.x,
      y: t.y,
      className: t.isVisible === false ? classnames(styles[t.style], styles.notVisible) : styles[t.style]
    };
    text.dominantBaseline = this.anchorToProps(t.anchor).dominantBaseline;
    text.textAnchor = this.anchorToProps(t.anchor).textAnchor;
    this.drawables.push(text);
    return this;
  }

  /// FF2-442 Attempt to provide scrollable tooltip to handle long skill descriptions
  /// --- [ TODO This doesn't work. Problems: ] -------------------------------------
  ///  o Cannot specify namespace on <div> in React (may not be an issue)
  ///  o Scrollbar does not seem to work even when permanently visible
  ///  o By its nature the scrollbar on a tooltip is unreachable, because as soon as
  ///    the mouse exits the trigger element, the tooltip is made invisible. We would
  ///    have to do one of
  ///      o handle a mouse move into the tooltip itself, and keep it visible if entered
  ///      o manage a scrolling action in the triggering element and transfer that into
  ///        the tooltip
  /// This is all too hard and not worth the effort, especially as the entire tooltip
  /// framework is against Accessibility guidelines, which we hope to implement later.
  /// --------------------------------------------------------------------------------
  addTextBox(t: TextBoxProps) {
    const textBox: TextBox = {
      kind: 'textBox',
      text: t.text,
      x: t.x,
      y: t.y,
      width: t.width,
      height: t.height,
      // This sets the Text style...
      style: t.isVisible === false ? classnames(styles[t.style], styles.notVisible) : styles[t.style],
      // ... and this sets the div style
      rectStyle: t.isVisible === false ? classnames(styles[t.rectStyle], styles.notVisible) : styles[t.rectStyle]
      // passed through this
      // className: (t.isVisible === false) ? classnames(styles[t.style], styles.notVisible) : styles[t.rectStyle],
    };
    this.drawables.push(textBox);
    return this;
  }

  addCustomElement(e: JSX.Element) {
    this.drawables.push({
      kind: 'custom',
      element: e
    });
  }

  /// Added key to this to shut React warnings about unique keys up
  /// FF2-436 added data-tour attribute for walkthrough
  /// FF2-442 Added <foreignobject> to nest (X)HTML elements. This is initially to work
  /// around the fact that overflow on SVG elements is not implemented yet. See above
  draw = (d: Drawable, key: number): JSX.Element => {
    if (d.kind === 'line') {
      return (
        <line
          key={key}
          className={classnames(styles.line, d.className)}
          x1={d.x1}
          y1={d.y1}
          x2={d.x2}
          y2={d.y2}
          strokeDasharray={d.strokeDashArray}
          strokeWidth={this.strokeWidth}
        />
      );
    } else if (d.kind === 'circle') {
      return (
        <circle
          key={key}
          className={classnames(styles.circle, d.className)}
          r={d.r}
          cx={d.x}
          cy={d.y}
          strokeWidth={this.strokeWidth}
        />
      );
    } else if (d.kind === 'triangle') {
      return (
        <polygon
          key={key}
          className={styles.triangle}
          strokeWidth={this.strokeWidth}
          points={`${d.p1.x},${d.p1.y} ${d.p2.x},${d.p2.y} ${d.p3.x},${d.p3.y}`}
        />
      );
    } else if (d.kind === 'rect') {
      const dataTourValue: string | undefined = d.id !== undefined ? `rect:${d.id}` : undefined;
      return (
        <rect
          key={key}
          className={classnames(styles.rectangle, d.className)}
          x={d.x}
          y={d.y}
          width={d.width}
          height={d.height}
          strokeWidth={this.strokeWidth}
          onMouseEnter={d.onMouseEnter}
          onMouseLeave={d.onMouseLeave}
          data-tour={dataTourValue}
        />
      );
    } else if (d.kind === 'label') {
      return (
        <text
          key={key}
          x={d.x}
          y={d.y}
          textAnchor={d.textAnchor}
          className={styles.label}
          dominantBaseline={d.dominantBaseline}
          onClick={d.onClick}
          data-tour={`label:${d.id}`}
        >
          <tspan
            x={d.x}
            y={d.y}
            dy={d.subtextDy}
            className={classnames(styles.label, styles.small, d.className)}
            data-tour={`label:${d.id}`}
          >
            {d.subtext}
          </tspan>
          <tspan
            x={d.x}
            y={d.y}
            dy={d.textDy}
            className={classnames(styles.label, styles.large)}
            data-tour={`label:${d.id}`}
          >
            {d.text}
          </tspan>
        </text>
      );
    } else if (d.kind === 'text') {
      return (
        <text
          key={key}
          x={d.x}
          y={d.y}
          textAnchor={d.textAnchor}
          className={classnames(styles.text, d.className)}
          dominantBaseline={d.dominantBaseline}
        >
          {d.text}
        </text>
      );
    } else if (d.kind === 'custom') {
      return d.element;
    } else if (d.kind === 'textBox') {
      return (
        <foreignObject key={key} x={d.x} y={d.y} width={d.width} height={d.height}>
          <div
            className={classnames(styles.textbox, d.rectStyle)}
            // Default... but you know
            style={{ overflowY: 'visible' }}
          // "scroll" generates a scroller widget which for some reason is entirely non-functional haha
          // style={{overflowY:"scroll"}}
          // Can't specify this in React before React 16
          // xmlns="http://www.w3.org/1999/xhtml"
          // TODO Styles including overflow=scroll
          >
            <span className={classnames(styles.text, d.style)}>{d.text}</span>
            {/* {d.text} */}
          </div>
        </foreignObject>
      );
    }
    throw Error('Unimplemented Drawable');
  };

  build(): JSX.Element {
    const posX = this.viewBox.posX;
    const posY = this.viewBox.posY;
    const dimX = this.viewBox.dimX;
    const dimY = this.viewBox.dimY;
    return (
      <svg width={'100%'} height={'100%'} viewBox={`${posX} ${posY} ${dimX} ${dimY}`}>
        {this.drawables.map((d, index) => this.draw(d, index))}
      </svg>
    );
  }
}

export interface ViewBox {
  posX: number;
  posY: number;
  dimX: number;
  dimY: number;
}

export type Drawable = Triangle | Circle | Line | Label | Rect | Text | TextBox | CustomElement;

export interface Triangle {
  kind: 'triangle';
  p1: Point;
  p2: Point;
  p3: Point;
}

export type TriangleProps = {
  size: number;
} & Point;

export type Circle = {
  kind: 'circle';
  r: number;
  className?: string;
} & Point;

export type CircleProps = {
  r: number;
  style: 'filled' | 'shaded' | 'background';
} & Point;

export interface Point {
  x: number;
  y: number;
}

export interface LineProps {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  style: 'primary' | 'solid' | 'dotted' | 'dashed';
  stroke?: 'faint' | 'light' | 'medium' | 'dark';
}

export interface Line {
  kind: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  strokeDashArray?: string;
  className?: string;
}

export type LabelProps = {
  text: string;
  subtext: string;
  anchor: Anchor;
  style: 'regular' | 'highlighted';
  onClick?(): void;
} & Point;

export type Label = {
  kind: 'label';
  text: string;
  subtext: string;
  textDy?: string;
  subtextDy?: string;
  textAnchor?: TextAnchor;
  dominantBaseline?: DominantBaseline;
  className?: string;
  onClick?(): void;
  id: number;
} & Point;

export type RectProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  style: 'white' | 'grey' | 'orange' | 'transparent';
  id?: number;
} & DrawProps;

export type Rect = {
  kind: 'rect';
  className?: string;
  id?: number;
} & Omit<RectProps, 'style'>;

export type TextProps = {
  text: string;
  anchor: Anchor;
  style: 'title' | 'subtitle' | 'detail' | 'marker';
  onClick?(): void;
} & Point &
  DrawProps;

export type Text = {
  kind: 'text';
  text: string;
  className?: string;
  textAnchor?: TextAnchor;
  dominantBaseline?: DominantBaseline;
} & Point;

/// FF2-442 see above
export type TextBoxProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  // Rect style
  rectStyle: 'white' | 'grey' | 'orange' | 'transparent';
  // Text style
  style: 'title' | 'subtitle' | 'detail' | 'marker';
} & Pick<DrawProps, 'isVisible'>;

export type TextBox = {
  kind: 'textBox';
  className?: string;
  id?: number;
  rectStyle: 'white' | 'grey' | 'orange' | 'transparent';
} & TextBoxProps;

export interface CustomElement {
  kind: 'custom';
  element: JSX.Element;
}
export type Anchor =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright'
  | 'center';
export type TextAnchor = 'start' | 'middle' | 'end';
export type DominantBaseline = 'baseline' | 'middle' | 'hanging';

export interface DrawProps {
  // showOnHover?: boolean;
  onMouseEnter?(): void;
  onMouseLeave?(): void;
  isVisible?: boolean;
}



// WEBPACK FOOTER //
// ./src/ui/components/svg-builder/svg-builder.tsx